import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import {
  CreditCard, CheckCircle, Crown, Rocket, Buildings,
  SpinnerGap, Receipt, Plus, Pencil, Trash, GearSix,
  Warning, Hourglass, Prohibit, TrendUp, ChartLine, Coin
} from '@phosphor-icons/react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const PLAN_ICONS = { basic: Buildings, pro: Rocket, enterprise: Crown };
const PLAN_COLORS = { basic: '#7367F0', pro: '#FF9F43', enterprise: '#28C76F' };

const BILLING_STATUS_CFG = {
  active:             { label: 'Active',          className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20', Icon: CheckCircle },
  trialing:           { label: 'Trial',           className: 'bg-blue-500/10 text-blue-600 border-blue-500/20',           Icon: Hourglass   },
  pending_payment:    { label: 'Pending Payment', className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: CreditCard  },
  past_due:           { label: 'Payment Failed',  className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  unpaid:             { label: 'Unpaid',          className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Warning     },
  trial_expired:      { label: 'Trial Expired',   className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
  canceled:           { label: 'Canceled',        className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',           Icon: Prohibit    },
  canceling:          { label: 'Canceling',       className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Hourglass   },
  incomplete:         { label: 'Incomplete',      className: 'bg-amber-500/10 text-amber-600 border-amber-500/20',        Icon: Warning     },
  incomplete_expired: { label: 'Expired',         className: 'bg-red-500/10 text-red-600 border-red-500/20',              Icon: Prohibit    },
};

export default function BillingPage() {
  const { apiCall } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState('tenants');
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantBilling, setTenantBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollingSessionId, setPollingSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  // Plan management
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [planForm, setPlanForm] = useState({ plan_id: '', name: '', price: '', stripe_price_id: '', price_per_user: true, max_projects: '', max_users: '', features: '', sort_order: '', is_active: true });
  const [deletePlan, setDeletePlan] = useState(null);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const sessionId = params.get('session_id');
    const status = params.get('status');
    if (sessionId && status === 'success') {
      setPollingSessionId(sessionId);
      pollPaymentStatus(sessionId, 0);
    } else if (status === 'cancelled') {
      setPaymentStatus({ type: 'cancelled', message: 'Payment was cancelled.' });
    }
  }, [params]);

  async function loadData() {
    try {
      const [plansRes, tenantsRes] = await Promise.all([apiCall('get', '/billing/plans/all'), apiCall('get', '/tenants')]);
      setPlans(plansRes.data || []);
      setTenants(tenantsRes.data || []);
      if (tenantsRes.data?.length > 0 && !selectedTenant) selectTenant(tenantsRes.data[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function selectTenant(t) {
    setSelectedTenant(t);
    try { const res = await apiCall('get', `/billing/tenant/${t.id}`); setTenantBilling(res.data); } catch (e) { console.error(e); }
  }

  async function pollPaymentStatus(sessionId, attempts) {
    if (attempts >= 5) { setPaymentStatus({ type: 'timeout', message: 'Payment status check timed out.' }); setPollingSessionId(null); return; }
    try {
      const res = await apiCall('get', `/billing/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') { setPaymentStatus({ type: 'success', message: 'Payment successful! Plan upgraded.' }); setPollingSessionId(null); loadData(); return; }
      if (res.data.status === 'expired') { setPaymentStatus({ type: 'expired', message: 'Session expired.' }); setPollingSessionId(null); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch { setPaymentStatus({ type: 'error', message: 'Error checking status.' }); setPollingSessionId(null); }
  }

  // --- Plan CRUD ---
  function openNewPlan() {
    setEditPlan(null);
    setPlanForm({ plan_id: '', name: '', price: '', stripe_price_id: '', price_per_user: true, max_projects: '', max_users: '', features: '', sort_order: '', is_active: true });
    setShowPlanDialog(true);
  }

  function openEditPlan(p) {
    setEditPlan(p);
    setPlanForm({
      plan_id: p.plan_id, name: p.name, price: String(p.price),
      stripe_price_id: p.stripe_price_id || '',
      price_per_user: p.price_per_user !== false,
      max_projects: String(p.max_projects), max_users: String(p.max_users),
      features: (p.features || []).join('\n'), sort_order: String(p.sort_order || 1), is_active: p.is_active !== false
    });
    setShowPlanDialog(true);
  }

  async function handleSavePlan() {
    const data = {
      plan_id: planForm.plan_id, name: planForm.name, price: parseFloat(planForm.price) || 0,
      stripe_price_id: (planForm.stripe_price_id || '').trim(),
      price_per_user: !!planForm.price_per_user,
      max_projects: parseInt(planForm.max_projects) || 5, max_users: parseInt(planForm.max_users) || 3,
      features: planForm.features.split('\n').map(f => f.trim()).filter(Boolean),
      sort_order: parseInt(planForm.sort_order) || 1, is_active: planForm.is_active
    };
    try {
      if (editPlan) { await apiCall('put', `/billing/plans/${editPlan.plan_id}`, data); }
      else { await apiCall('post', '/billing/plans', data); }
      setShowPlanDialog(false); loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save plan'); }
  }

  async function handleDeletePlan() {
    if (!deletePlan) return;
    try { await apiCall('delete', `/billing/plans/${deletePlan.plan_id}`); setDeletePlan(null); loadData(); } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  }

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const currentPlan = tenantBilling?.current_plan || 'basic';
  const activePlans = plans.filter(p => p.is_active);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fadeIn" data-testid="billing-page">
      {paymentStatus && (
        <div className={`p-4 rounded-lg border ${paymentStatus.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'}`}>
          <div className="flex items-center gap-2">{paymentStatus.type === 'success' && <CheckCircle size={20} weight="fill" />}<span className="font-medium">{paymentStatus.message}</span></div>
        </div>
      )}
      {pollingSessionId && <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 flex items-center gap-2"><SpinnerGap size={20} className="animate-spin" /><span className="font-medium">Verifying payment...</span></div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold" data-testid="billing-heading">Subscription & Billing</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage plans and tenant subscriptions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button onClick={() => setTab('tenants')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'tenants' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-tenant-billing">Tenant Billing</button>
        <button onClick={() => setTab('plans')} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === 'plans' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-manage-plans">Manage Plans ({plans.length})</button>
      </div>

      {/* ========== TENANT BILLING TAB ========== */}
      {tab === 'tenants' && (
        <>
          {tenants.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-muted-foreground">Tenant:</label>
              <select
                value={selectedTenant?.id || ''}
                onChange={e => {
                  const t = tenants.find(x => x.id === e.target.value);
                  if (t) selectTenant(t);
                }}
                className="h-9 text-sm rounded-md border border-input bg-background px-3 min-w-[260px] max-w-md"
                data-testid="billing-tenant-select"
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{t.organization || t.name}</option>
                ))}
              </select>
            </div>
          )}
          {selectedTenant && tenantBilling && (
            <>
              <Card className="shadow-card rounded-lg border-0 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${PLAN_COLORS[currentPlan] || '#7367F0'}20` }}>
                        {React.createElement(PLAN_ICONS[currentPlan] || Buildings, { size: 24, style: { color: PLAN_COLORS[currentPlan] || '#7367F0' }, weight: 'duotone' })}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold">{selectedTenant.organization}</h3>
                          <Badge className="capitalize" style={{ backgroundColor: `${PLAN_COLORS[currentPlan] || '#7367F0'}20`, color: PLAN_COLORS[currentPlan] || '#7367F0' }}>{currentPlan}</Badge>
                          {(() => {
                            const status = selectedTenant.subscription_status;
                            if (!status) return null;
                            const cfg = BILLING_STATUS_CFG[status] || { label: status.replace('_',' '), className: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20', Icon: Warning };
                            const Icon = cfg.Icon;
                            return (
                              <Badge className={`text-xs ${cfg.className}`} data-testid={`tenant-billing-status-${status}`}>
                                <Icon size={12} className="mr-1" /> {cfg.label}
                              </Badge>
                            );
                          })()}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {tenantBilling.usage.users} / {tenantBilling.plan_details.max_users >= 999999 ? 'Unlimited' : tenantBilling.plan_details.max_users} users, {tenantBilling.usage.projects} / {tenantBilling.plan_details.max_projects >= 999999 ? 'Unlimited' : tenantBilling.plan_details.max_projects} projects
                        </p>
                      </div>
                    </div>
                    {(() => {
                      const status = selectedTenant.subscription_status;
                      const needsCardFix = ['past_due', 'unpaid', 'pending_payment'].includes(status);
                      if (needsCardFix && tenantBilling?.has_stripe_customer) {
                        return (
                          <Button
                            variant="default"
                            onClick={async () => {
                              try {
                                const res = await apiCall('post', '/billing/portal', { tenant_id: selectedTenant.id, return_url: window.location.href });
                                if (res.data?.url) window.location.href = res.data.url;
                              } catch (e) { alert(e.response?.data?.detail || 'Unable to open Stripe Customer Portal'); }
                            }}
                            className="rounded-md bg-amber-500 hover:bg-amber-600 text-white"
                            data-testid="manage-plan-update-card-btn"
                          >
                            <CreditCard size={16} className="mr-1" /> Update Card
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="grid grid-cols-2 gap-6 mt-6">
                    {['users', 'projects'].map(key => {
                      const val = tenantBilling.usage[key];
                      const max = key === 'users' ? tenantBilling.plan_details.max_users : tenantBilling.plan_details.max_projects;
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-xs mb-1.5"><span className="font-medium text-muted-foreground capitalize">{key}</span><span className="font-bold">{val} / {max >= 999999 ? '∞' : max}</span></div>
                          <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${Math.min(100, (val / (max >= 999999 ? 100 : max)) * 100)}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Revenue summary (replaces plan cards for admin) */}
              {(() => {
                const txs = (tenantBilling.transactions || []).filter(t => t.payment_status === 'paid');
                const lifetimeValue = txs.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                const mrr = (tenantBilling.total_monthly || tenantBilling.plan_details?.price || 0);

                // Group last 6 months of paid invoices
                const months = [];
                const now = new Date();
                for (let i = 5; i >= 0; i--) {
                  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                  months.push({
                    key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                    label: d.toLocaleDateString('en-US', { month: 'short' }),
                    revenue: 0
                  });
                }
                txs.forEach(t => {
                  if (!t.created_at) return;
                  const d = new Date(t.created_at);
                  const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  const m = months.find(x => x.key === key);
                  if (m) m.revenue += parseFloat(t.amount) || 0;
                });
                const last6Total = months.reduce((s, m) => s + m.revenue, 0);

                return (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* MRR */}
                    <Card className="shadow-card rounded-lg border-0" data-testid="revenue-mrr-card">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Monthly Recurring</div>
                          <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                            <Coin size={18} className="text-primary" weight="duotone" />
                          </div>
                        </div>
                        <div className="mt-3 text-3xl font-bold">${mrr.toFixed(2)}</div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {tenantBilling.plan_details.price_per_user
                            ? `$${tenantBilling.plan_details.price?.toFixed(0)} × ${tenantBilling.usage.users} users`
                            : `Flat ${tenantBilling.plan_details.name} plan`}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Lifetime value */}
                    <Card className="shadow-card rounded-lg border-0" data-testid="revenue-ltv-card">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Lifetime Revenue</div>
                          <div className="w-9 h-9 rounded-md bg-emerald-500/10 flex items-center justify-center">
                            <TrendUp size={18} className="text-emerald-500" weight="duotone" />
                          </div>
                        </div>
                        <div className="mt-3 text-3xl font-bold">${lifetimeValue.toFixed(2)}</div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {txs.length} paid invoice{txs.length === 1 ? '' : 's'}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Last 6 months chart */}
                    <Card className="shadow-card rounded-lg border-0" data-testid="revenue-6m-card">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last 6 Months</div>
                            <div className="text-lg font-bold mt-1">${last6Total.toFixed(2)}</div>
                          </div>
                          <div className="w-9 h-9 rounded-md bg-amber-500/10 flex items-center justify-center">
                            <ChartLine size={18} className="text-amber-500" weight="duotone" />
                          </div>
                        </div>
                        <div className="h-[80px] -mx-1 mt-1">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={months} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8e6f1" />
                              <XAxis dataKey="label" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                              <YAxis hide />
                              <Tooltip
                                formatter={(v) => [`$${(v || 0).toFixed(2)}`, 'Revenue']}
                                contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e8e6f1' }}
                              />
                              <Bar dataKey="revenue" fill="#7367F0" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {tenantBilling.transactions?.length > 0 && (
                <Card className="shadow-card rounded-lg border-0 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="px-6 py-4 border-b border-border"><h3 className="text-sm font-bold flex items-center gap-2"><Receipt size={16} /> Payment History</h3></div>
                    <Table>
                      <TableHeader><TableRow className="bg-secondary/50"><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Date</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Amount</TableHead><TableHead className="text-xs font-semibold text-muted-foreground uppercase">Status</TableHead></TableRow></TableHeader>
                      <TableBody>{tenantBilling.transactions.map((tx, i) => (
                        <TableRow key={tx.id || i}>
                          <TableCell className="text-sm">{tx.created_at ? new Date(tx.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                          <TableCell><Badge variant="outline" className="capitalize text-xs">{tx.plan_id}</Badge></TableCell>
                          <TableCell className="text-sm font-medium">${tx.amount?.toFixed(2)}</TableCell>
                          <TableCell>{tx.payment_status === 'paid' ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs"><CheckCircle size={12} className="mr-1" /> Paid</Badge> : <Badge variant="secondary" className="text-xs">{tx.payment_status}</Badge>}</TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </>
      )}

      {/* ========== MANAGE PLANS TAB ========== */}
      {tab === 'plans' && (
        <>
          <div className="flex justify-end">
            <Button onClick={openNewPlan} className="rounded-md" data-testid="create-plan-btn"><Plus size={16} className="mr-2" weight="bold" /> New Plan</Button>
          </div>

          <Card className="shadow-card rounded-lg border-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Order</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Plan</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Price</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Projects</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Users</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Features</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-muted-foreground uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map(p => (
                  <TableRow key={p.plan_id} className="hover:bg-secondary/30" data-testid={`plan-row-${p.plan_id}`}>
                    <TableCell className="text-sm font-medium">{p.sort_order}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {React.createElement(PLAN_ICONS[p.plan_id] || CreditCard, { size: 16, style: { color: PLAN_COLORS[p.plan_id] || '#7367F0' }, weight: 'duotone' })}
                        <div><div className="font-medium">{p.name}</div><div className="text-[10px] text-muted-foreground font-mono">{p.plan_id}</div></div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-bold">${p.price?.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">/mo</span></TableCell>
                    <TableCell className="text-sm">{p.max_projects >= 999999 ? '∞' : p.max_projects}</TableCell>
                    <TableCell className="text-sm">{p.max_users >= 999999 ? '∞' : p.max_users}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{(p.features || []).join(', ')}</TableCell>
                    <TableCell>{p.is_active !== false ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">Active</Badge> : <Badge variant="secondary" className="text-xs">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditPlan(p)} className="h-8 w-8 p-0" data-testid={`edit-plan-${p.plan_id}`}><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeletePlan(p)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" data-testid={`delete-plan-${p.plan_id}`}><Trash size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Plan Create/Edit Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-lg" data-testid="plan-dialog">
          <DialogHeader><DialogTitle><GearSix size={20} className="inline mr-2 text-primary" />{editPlan ? 'Edit Plan' : 'New Plan'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Plan ID</Label>
                <Input value={planForm.plan_id} onChange={e => setPlanForm(p => ({ ...p, plan_id: e.target.value }))} placeholder="e.g. starter" disabled={!!editPlan} className="rounded-md font-mono text-sm" data-testid="plan-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Display Name</Label>
                <Input value={planForm.name} onChange={e => setPlanForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Starter" className="rounded-md" data-testid="plan-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Price ($/mo)</Label>
                <Input type="number" step="0.01" value={planForm.price} onChange={e => setPlanForm(p => ({ ...p, price: e.target.value }))} placeholder="29" className="rounded-md" data-testid="plan-price-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Max Projects</Label>
                <Input type="number" value={planForm.max_projects} onChange={e => setPlanForm(p => ({ ...p, max_projects: e.target.value }))} placeholder="10" className="rounded-md" data-testid="plan-projects-input" />
                <p className="text-[10px] text-muted-foreground">999999 = Unlimited</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Max Users</Label>
                <Input type="number" value={planForm.max_users} onChange={e => setPlanForm(p => ({ ...p, max_users: e.target.value }))} placeholder="5" className="rounded-md" data-testid="plan-users-input" />
                <p className="text-[10px] text-muted-foreground">999999 = Unlimited</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Sort Order</Label>
                <Input type="number" value={planForm.sort_order} onChange={e => setPlanForm(p => ({ ...p, sort_order: e.target.value }))} placeholder="1" className="rounded-md" />
              </div>
              <div className="space-y-1.5 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={planForm.is_active} onChange={e => setPlanForm(p => ({ ...p, is_active: e.target.checked }))} className="w-4 h-4 rounded accent-primary" />
                  <span className="text-sm font-medium">Active (visible to tenants)</span>
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Stripe Price ID <span className="text-muted-foreground font-normal">(optional — enables recurring subscriptions)</span></Label>
              <Input value={planForm.stripe_price_id} onChange={e => setPlanForm(p => ({ ...p, stripe_price_id: e.target.value }))} placeholder="price_1Q8abcXYZ..." className="rounded-md font-mono text-xs" data-testid="plan-stripe-price-input" />
              <p className="text-[10px] text-muted-foreground">Create a recurring Price in Stripe Dashboard → Products, then paste the Price ID here. Leave blank for one-time checkout.</p>
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={planForm.price_per_user} onChange={e => setPlanForm(p => ({ ...p, price_per_user: e.target.checked }))} className="w-4 h-4 rounded accent-primary" />
                <span className="text-sm font-medium">Per-user billing (price × number of users)</span>
              </label>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Features (one per line)</Label>
              <textarea value={planForm.features} onChange={e => setPlanForm(p => ({ ...p, features: e.target.value }))} rows={4} placeholder="Feature 1&#10;Feature 2&#10;Feature 3" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y" data-testid="plan-features-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSavePlan} className="rounded-md" data-testid="plan-save-btn">{editPlan ? 'Save Changes' : 'Create Plan'}</Button>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog open={!!deletePlan} onOpenChange={(o) => !o && setDeletePlan(null)}
        title="Delete Plan" description={`Delete "${deletePlan?.name}" plan? This cannot be undone. Plans with active tenants cannot be deleted.`}
        onConfirm={handleDeletePlan} destructiveLabel="Delete Plan" />
    </div>
  );
}
